import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchTabUsers } from "services/signontab.service";

export type SignMethodValue =
  | { method: "dokobit" }
  | { method: "manual" }
  | { method: "signontab"; selectedTabUser: string };

type SignOnTabTablet = { tabId: string; name: string };

interface SignMethodSelectProps {
  value: SignMethodValue;
  onChange: (value: SignMethodValue) => void;
  companyId: string;
}

export default function SignMethodSelect({
  value,
  onChange,
  companyId,
}: SignMethodSelectProps) {
  const { t } = useTranslation();

  // The company's tablets are loaded once, the first time SignOnTab is picked.
  // The caller passes key={companyId}, so another company starts from scratch.
  const [tablets, setTablets] = useState<SignOnTabTablet[] | null>(null);
  const [failed, setFailed] = useState(false);
  const wantsSignOnTab = value.method === "signontab";
  const chosenTablet = value.method === "signontab" ? value.selectedTabUser : "";

  useEffect(() => {
    if (!wantsSignOnTab || tablets !== null || failed) return;
    let cancelled = false;
    fetchTabUsers(companyId)
      .then((data: SignOnTabTablet[]) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setTablets(list);
        // The view can mount this select with a tablet already chosen: keep it.
        const chosenTabletIsListed = list.some((tablet) => tablet.tabId === chosenTablet);
        if (list.length > 0 && !chosenTabletIsListed) onChange({ method: "signontab", selectedTabUser: list[0].tabId });
      })
      .catch(() => {
        // Not "no tablets": keep the list unloaded and offer a retry.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // onChange is called once, when the list arrives: a new callback or tablet choice must not restart the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsSignOnTab, tablets, failed, companyId]);

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("signing_method")}
        </label>
        <select
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={value.method}
          onChange={(e) => {
            const method = e.target.value as SignMethodValue["method"];
            // The first tablet is chosen for HR once the list is there. Until then the value
            // carries an empty one, which keeps "send" off.
            onChange(method === "signontab" ? { method, selectedTabUser: tablets?.[0]?.tabId ?? "" } : { method });
          }}
        >
          <option value="dokobit">Dokobit</option>
          <option value="signontab">SignOnTab</option>
          <option value="manual">{t("manual_signature") || "Manual"}</option>
        </select>
      </div>
      {value.method === "signontab" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("amendment_signontab_tablet")}
          </label>
          {failed ? (
            <p className="py-2 text-sm text-red-600">
              {t("amendment_signontab_tablets_failed")}{" "}
              <button type="button" className="underline hover:text-red-700" onClick={() => setFailed(false)}>
                {t("retry")}
              </button>
            </p>
          ) : tablets === null ? (
            <p className="py-2 text-sm text-gray-500">{t("loading")}</p>
          ) : tablets.length === 0 ? (
            <p className="py-2 text-sm text-red-600">{t("amendment_signontab_no_tablets")}</p>
          ) : (
            <select
              aria-label={t("amendment_signontab_tablet")}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={value.selectedTabUser}
              onChange={(e) => onChange({ method: "signontab", selectedTabUser: e.target.value })}
            >
              {tablets.map((tablet) => (
                <option key={tablet.tabId} value={tablet.tabId}>
                  {tablet.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </>
  );
}
